import Spinner from '@/components/Spinner';
import ShadCnFormInput from '@/components/FormComponents/ShadCnFormInput';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useGetTransferQuestions, useSetTransferQuestions } from '@/hooks/useTransferQuestions';
import { useListIntegrations } from '@/hooks/useIntegrationsQuery';
import { ISetTransferQuestionsBody } from '@/services/transferQuestions';
import { handleShowError } from '@/utils';
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Plus, Users, X,
} from 'lucide-react';
import { FC, useEffect, useState, useCallback } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useAnalytics } from '@/hooks/useAnalytics';
import { CHATBOT_HANDOFF_CONFIGURED } from '@/lib/analytics-events';
import SectionCard from './shared/SectionCard';
import RadioCard from './shared/RadioCard';
import StickyActionBar from './shared/StickyActionBar';

interface HumanTransferProps {
  token: string;
}

interface ITransferForm {
  questions: {
    question_text: string;
  }[];
}

const HumanTransfer: FC<HumanTransferProps> = ({ token }) => {
  const { capture } = useAnalytics();
  const [transferPlatform, setTransferPlatform] = useState<'teams' | 'slack'>('teams');
  const [savedPlatform, setSavedPlatform] = useState<'teams' | 'slack'>('teams');
  const [handoffEnabled, setHandoffEnabled] = useState(true);
  const [savedEnabled, setSavedEnabled] = useState(true);

  const form = useForm<ITransferForm>({
    defaultValues: {
      questions: [{ question_text: '' }],
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = form;
  const { fields, append, remove, swap } = useFieldArray({
    control,
    name: 'questions',
  });

  const { data: transferPayload, isLoading: isLoadingQuestions } = useGetTransferQuestions(token);
  const { mutate: setQuestionsMutate, isPending: isSettingQuestions } = useSetTransferQuestions();

  // Proactively reflect whether each handoff platform is actually connected in Integrations, so the
  // user knows BEFORE saving (previously this only surfaced as a post-save error toast).
  const { data: integrationsData } = useListIntegrations();
  const teamsConnected = !!integrationsData?.integrations?.find((i) => i.platform === 'teams_chat');
  const slackConnected = !!integrationsData?.integrations?.find((i) => i.platform === 'slack_chat');

  // Load fetched questions + platform into the form.
  useEffect(() => {
    if (!transferPayload) return;
    const list = transferPayload.questions ?? [];
    reset({
      questions: list.length > 0
        ? list.map((q) => ({ question_text: q.question_text }))
        : [{ question_text: '' }],
    });
    const p = transferPayload.platform === 'slack' ? 'slack' : 'teams';
    setTransferPlatform(p);
    setSavedPlatform(p);
    const en = transferPayload.enabled !== false;
    setHandoffEnabled(en);
    setSavedEnabled(en);
  }, [transferPayload, reset]);

  const onSubmit = (data: ITransferForm) => {
    // Drop empty + duplicate questions; the backend treats an empty array as "remove all".
    const seen = new Set<string>();
    const questionsPayload = data.questions
      .filter((q) => {
        const text = q.question_text.trim();
        if (!text || seen.has(text.toLowerCase())) return false;
        seen.add(text.toLowerCase());
        return true;
      })
      .map((q, index) => ({
        question_text: q.question_text,
        question_order: index + 1,
        is_required: true,
        input_type: 'text',
        is_active: true,
      }));

    const body: ISetTransferQuestionsBody = {
      chat_token: token,
      platform: transferPlatform,
      questions: questionsPayload,
      enabled: handoffEnabled,
    };

    setQuestionsMutate(body, {
      onSuccess: () => {
        capture(CHATBOT_HANDOFF_CONFIGURED, {
          platform: transferPlatform,
          question_count: questionsPayload.length,
          enabled: handoffEnabled,
        });
        setSavedPlatform(transferPlatform);
        setSavedEnabled(handoffEnabled);
        reset(data); // new baseline so the form is no longer dirty
        toast.success('Handoff settings saved');
      },
      onError: (error) => {
        const err = error as { status?: number; data?: unknown };
        const data2 = typeof err?.data === 'string' ? err.data : '';
        if (
          err?.status === 400
          && (data2.includes('No active slack installation found')
            || data2.includes('No active teams installation found'))
        ) {
          const platform = data2.includes('slack') ? 'Slack' : 'Teams';
          toast.error(`No active ${platform} installation found, please add this in Integrations`);
        } else {
          handleShowError(error);
        }
      },
    });
  };

  const addNewQuestion = useCallback(() => append({ question_text: '' }), [append]);
  const handleRemoveItem = useCallback((index: number) => remove(index), [remove]);
  const moveQuestion = useCallback(
    (index: number, direction: 'up' | 'down') => {
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target >= 0 && target < fields.length) swap(index, target);
    },
    [swap, fields.length],
  );

  const handleCancel = useCallback(() => {
    if (!transferPayload) return;
    const list = transferPayload.questions ?? [];
    reset({
      questions: list.length > 0
        ? list.map((q) => ({ question_text: q.question_text }))
        : [{ question_text: '' }],
    });
    setTransferPlatform(savedPlatform);
    setHandoffEnabled(savedEnabled);
  }, [reset, transferPayload, savedPlatform, savedEnabled]);

  const platformDirty = transferPlatform !== savedPlatform;
  const enabledDirty = handoffEnabled !== savedEnabled;

  // Warn (don't block) when handoff is on but the chosen platform isn't connected — the vendor may
  // legitimately set this up before finishing the Integrations install, and the backend is the
  // authority. Only show once the integrations list has actually loaded.
  const chosenConnected = transferPlatform === 'teams' ? teamsConnected : slackConnected;
  const showConnectWarning = handoffEnabled && integrationsData != null && !chosenConnected;
  const chosenPlatformLabel = transferPlatform === 'teams' ? 'Microsoft Teams' : 'Slack';

  const connectedBadge = (connected: boolean) => (connected
    ? <Badge variant="success" size="xs" icon={CheckCircle2}>Connected</Badge>
    : <Badge variant="warning" size="xs">Not connected</Badge>);

  if (isLoadingQuestions) {
    return (
      <div className="flex items-center justify-center p-10">
        <Spinner />
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          <SectionCard
            icon={Users}
            title="Human Handoff"
            subtitle="Route conversations to a live agent and collect context before the handoff."
          >
            {/* Enable toggle */}
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">Enable Human Handoff</p>
                <p className="mt-0.5 text-[13px] text-gray-500">
                  Let visitors ask to speak with a live person. When off, the bot never offers a handoff.
                </p>
              </div>
              <Switch
                checked={handoffEnabled}
                onCheckedChange={setHandoffEnabled}
                aria-label="Enable Human Handoff"
                className="mt-0.5 shrink-0"
              />
            </div>

            {/* Config — collapsed entirely when handoff is off (matches the other bot tabs and
                avoids leaving an inert, half-focusable subtree on screen). */}
            {handoffEnabled && (
            <div className="space-y-6">
            {/* Platform */}
            <div>
              <p className="text-sm font-medium text-gray-900">Handoff platform</p>
              <p className="mt-0.5 text-[13px] text-gray-500">
                Where conversations are routed when a visitor is handed off to your team.
              </p>
              <div role="radiogroup" aria-label="Handoff platform" className="mt-3 grid gap-3 sm:grid-cols-2">
                <RadioCard
                  checked={transferPlatform === 'teams'}
                  onSelect={() => setTransferPlatform('teams')}
                  title="Microsoft Teams"
                  subtitle="Route handoffs to a Teams channel"
                  trailing={connectedBadge(teamsConnected)}
                />
                <RadioCard
                  checked={transferPlatform === 'slack'}
                  onSelect={() => setTransferPlatform('slack')}
                  title="Slack"
                  subtitle="Route handoffs to a Slack channel"
                  trailing={connectedBadge(slackConnected)}
                />
              </div>
            </div>

            {/* Pre-handoff questions */}
            <div>
              <p className="text-sm font-medium text-gray-900">Pre-handoff questions</p>
              <p className="mt-0.5 text-[13px] text-gray-500">
                Asked before the conversation is handed off, in the order shown.
              </p>

              <div className="mt-3 space-y-2">
                {fields.length === 0 && (
                  <p className="rounded-lg border border-dashed border-gray-200 py-4 text-center text-[13px] text-gray-400">
                    No questions yet — visitors are connected to your team right away.
                  </p>
                )}
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2"
                  >
                    <span className="w-5 shrink-0 text-center text-[13px] font-medium text-gray-400">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <ShadCnFormInput
                        name={`questions.${index}.question_text`}
                        placeholder="e.g. What are you looking to solve?"
                        aria-label={`Question ${index + 1}`}
                        maxLength={200}
                      />
                    </div>
                    <div className="flex shrink-0 items-center gap-0.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => moveQuestion(index, 'up')}
                        disabled={index === 0}
                        aria-label={`Move question ${index + 1} up`}
                        className="h-8 w-8 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      >
                        <ChevronUp size={16} />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => moveQuestion(index, 'down')}
                        disabled={index === fields.length - 1}
                        aria-label={`Move question ${index + 1} down`}
                        className="h-8 w-8 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      >
                        <ChevronDown size={16} />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveItem(index)}
                        aria-label={`Remove question ${index + 1}`}
                        className="h-8 w-8 text-gray-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addNewQuestion}
                aria-label="Add question"
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:border-primary/40 hover:bg-primary/[0.04] hover:text-primary"
              >
                <Plus size={16} /> Add question
              </button>
            </div>
            </div>
            )}
          </SectionCard>
        </div>

        <StickyActionBar
          onReset={handleCancel}
          onSave={handleSubmit(onSubmit)}
          saving={isSettingQuestions}
          saveDisabled={!isDirty && !platformDirty && !enabledDirty}
          resetDisabled={isSettingQuestions}
          leftSlot={showConnectWarning ? (
            <div className="flex items-center gap-2 text-sm text-amber-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>
                {chosenPlatformLabel} isn&apos;t connected.{' '}
                <a href="/integrations" className="font-medium underline">Set it up in Integrations</a>
                {' '}to receive handoffs.
              </span>
            </div>
          ) : undefined}
        />
      </form>
    </FormProvider>
  );
};

export default HumanTransfer;
